import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';
import {
  SelectorAutocompleteDeferredItemComponent,
} from "./selector-autocomplete-deferred-item/selector-autocomplete-deferred-item.component";
import { SharedComponentsModule } from "../../../module/shared-components.module";
import { SelectorAutocompletePickerWrapper, SelectorProps } from "./common";

@Component({
  selector: 'app-selector-autocomplete-deferred',
  templateUrl: './selector-autocomplete-deferred.component.html',
  styleUrls: ['./selector-autocomplete-deferred.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => SelectorAutocompleteDeferredComponent),
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SelectorAutocompleteDeferredItemComponent,
    SharedComponentsModule,
  ],
})

/**
 * Functionality is similar to {@link SelectorAutocompleteComponent} but the data is limited to be only a map with
 * {@link SelectorAutocompletePickerWrapper} as keys for simplicity. The difference is that this component utilizes
 * {@link SelectorAutocompleteDeferredItemComponent} to load the data only when it appears on the screen.
 *
 * @Input {@link data} is a map with data for list, key will be displayed, value will be emitted upon selection.
 * @Input {@link field} is used to display an object concrete field when data is an array of objects.
 * Example: field: `name `, data: `{uuid: UUID, name: string ...}`.
 * @Input {@link title} is title above the selector
 * @Input {@link outerClass} any css class to be applied to the outer div wrapper
 * @Input {@link selectedItem} to pre-set selected item that will be displayed
 * @Input {@link props} small properties of the selector, such as max entries length and if it should be opened on hover.
 * props.openOnClick is ignored for the sake of simplicity.
 * @Input {@link isInsideModal} set true if selector is inside of modal
 * @Output {@link valueEmitter} emits new selected value
 * @var {@link isDataReadyForPickerCssStructure} tells if the data has header and subheader, as it is e.x. done in the hornavbar fund selector.
 * See {@link SelectorAutocompletePickerWrapper} for reference.
 */
export class SelectorAutocompleteDeferredComponent implements OnChanges {
  @Input({ required: true }) data: Map<SelectorAutocompletePickerWrapper, any>;
  @Input() field = '';
  @Input() title = '';
  @Input() outerClass = 'control-container';
  @Input() selectedItem: string;
  @Input() props = new SelectorProps();
  @Input() isInsideModal = false;
  @Input({ required: true }) onLoadAction: (item: SelectorAutocompletePickerWrapper, value: any) => Promise<void>;

  @Output() valueEmitter = new EventEmitter<any>();
  isSimpleArray = true;
  showPopUpModal = false;
  searchInputValue$ = new BehaviorSubject<any>('');

  dataKeys: SelectorAutocompletePickerWrapper[];
  dataValues: string[];
  filteredData: Observable<any[]>;

  setSelected(value: SelectorAutocompletePickerWrapper): void {
    this.selectedItem = this.isSimpleArray ? value : value[this.field];
    this.searchInputValue$.next('');
    const valueToEmit = this.data.get(value);
    this.valueEmitter.emit(valueToEmit);
    this.onChange(valueToEmit);
    this.showPopUpModal = false;
  }

  setFilteredData(): void {
    this.filteredData = this.searchInputValue$
      .asObservable().pipe(
        startWith(''),
        map((value: string): any[] => this.filter(value)),
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field) {
      this.isSimpleArray = (this.field === '');
    }
    if (changes.data && this.data) {
      this.dataKeys = Array.from(this.data.keys());
      this.dataValues = Array.from(this.data.values());
      this.setFilteredData();
    }
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  writeValue(value: any): void {
    this.selectedItem = value;
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChange = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(value: any): void {
  }

  onTouched(): void {
  }

  private filter(value: string): any[] {
    if (!this.dataKeys) {
      return [];
    }
    const filterValue = value?.toString().toLowerCase();
    return this.dataKeys.filter((wrapper) => {
      return wrapper.header?.toLowerCase().includes(filterValue) || wrapper.subheader?.toLowerCase().includes(filterValue);
    });
  }
}
